import type { JSX } from 'react';
import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { VerificationStatusBadge } from './VerificationStatusBadge';
import { VerificationActions } from './VerificationActions';
import { ReturnedComment } from './ReturnedComment';
import { RefereeResponse, RefereeResponseToggle } from './RefereeResponse';
import { hasRefereeResponse } from '../utils/refereeResponse';
import type { Verification } from '../types/verification.types';

interface VerificationCardProps {
  verification: Verification;
  onDelete: () => void;
  onCopyRefereeLink: () => void;
}

export function VerificationCard({
  verification,
  onDelete,
  onCopyRefereeLink,
}: VerificationCardProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const showResponse = hasRefereeResponse(verification);
  const hasDetails =
    Boolean(verification.refereeJobTitle) || verification.status === 'RETURNED' || showResponse;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{verification.refereeName}</span>
          <span className="text-sm text-muted-foreground">{verification.refereeEmail}</span>
        </div>
        <div className="flex items-center gap-2">
          <VerificationStatusBadge status={verification.status} />
          <VerificationActions onDelete={onDelete} onCopyRefereeLink={onCopyRefereeLink} />
        </div>
      </CardHeader>
      {hasDetails && (
        <CardContent className="flex flex-col gap-1 pt-0">
          {verification.refereeJobTitle && (
            <span className="text-sm text-muted-foreground">{verification.refereeJobTitle}</span>
          )}
          <ReturnedComment status={verification.status} comment={verification.inspectionComment} />
          {showResponse && (
            <div className="flex flex-col gap-1">
              <RefereeResponseToggle
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded((expanded) => !expanded)}
              />
              {isExpanded && <RefereeResponse verification={verification} />}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
